<template>
  <v-card>
    <v-row
      class="ma-0 px-4 pt-5"
      :class="{ 'pb-5': $vuetify.breakpoint.smAndDown }"
    >
      <v-col cols="12" md="4" lg="4">
        <AssigneesSelectorForm
          data-testid="recognitions-filter-accounts"
          class="mb-0"
          :label="$t('people.people')"
          :availableAccounts="availableAccounts"
          :isLoading="isLoadingAccounts"
          :selectedAccountIds="filters.account"
          @search="searchAccounts"
          @change="onAccountChange"
        >
          <UserAvatar
            v-if="filters.account"
            data-testid="recognitions-component-userAvatar"
            size="24px"
            fontSize="10px"
            class="mr-1"
            :account="selectedAccount"
          />
          <v-icon
            v-else
            size="24"
            color="blue-grey darken-1"
            class="mr-1"
            data-testid="recognitions-icon-companyWide"
            >mdi-web</v-icon
          >
        </AssigneesSelectorForm>
      </v-col>
      <v-col
        cols="12"
        md="4"
        lg="4"
        :class="{ 'mt-n2': $vuetify.breakpoint.smAndDown }"
      >
        <v-select
          data-testid="recognitions-select-type"
          class="mb-0"
          dense
          outlined
          hide-details
          label=""
          :placeholder="filters.type ? '' : $t('recognitions.all_recognitions')"
          :disabled="!filters.account"
          :items="availableTypes"
          :value="filters.type"
          @change="onTypeChange"
        >
          <template v-slot:prepend-inner>
            <v-icon
              :color="
                filters.account ? 'blue-grey darken-1' : 'blue-grey lighten-1'
              "
              size="24"
              class="mr-1"
              >mdi-medal-outline</v-icon
            >
          </template>
        </v-select>
      </v-col>
      <v-col
        cols="12"
        md="4"
        lg="4"
        :class="{ 'mt-3': $vuetify.breakpoint.smAndDown }"
      >
        <v-menu offset-y :close-on-content-click="false">
          <template v-slot:activator="{ on }">
            <v-text-field
              data-testid="recognitions-select-dateRange"
              v-model="dateRangeText"
              dense
              outlined
              readonly
              hide-details
              clearable
              v-on="on"
              event-color="primary"
              :label="$t('recognitions.time_period')"
              @click:clear="onDateRangeChange(null)"
            >
              <template v-slot:prepend-inner>
                <v-icon color="blue-grey darken-1" size="24" class="mr-1"
                  >mdi-calendar</v-icon
                >
              </template>
            </v-text-field>
          </template>
          <v-date-picker
            v-model="dates"
            no-title
            range
            reactive
            :allowed-dates="isDateAllowed"
            @change="onDateRangeChange"
          ></v-date-picker>
        </v-menu>
      </v-col>
    </v-row>
  </v-card>
</template>
<script>
import { mapGetters } from 'vuex'
import * as moment from 'moment'

import api from '@/api'
import { utils } from 'frontend-js-shared'
import mixin from '@/mixins/mixin.vue'

import AssigneesSelectorForm from '@/components/AssigneesSelector/AssigneesSelectorForm.vue'
import UserAvatar from '@/components/UserAvatar.vue'

const DEFAULT_SORT = 'createdAt'
const SORT_BY_COINCIDENCE = '_score'

export default {
  name: 'RecognitionsFilters',

  mixins: [mixin],

  components: {
    AssigneesSelectorForm,
    UserAvatar,
  },

  data() {
    return {
      filters: {
        account: null,
        type: null,
        date: {},
      },
      availableAccounts: [],
      isLoadingAccounts: false,
      dates: [],
      today: moment().format('YYYY-MM-DD'),
      selectedAccount: {},
    }
  },

  watch: {
    '$route.query': {
      handler() {
        this.setQueryFilters()
        this.parseAndEmitFilters()
      },
      deep: true,
    },
  },

  created() {
    this.setQueryFilters()
    this.parseAndEmitFilters()
    this.searchAccounts()
  },

  computed: {
    ...mapGetters(['account', 'organizationId']),

    availableTypes() {
      return [
        {
          text: this.$t('recognitions.all_recognitions'),
          value: '',
        },
        {
          text: this.$t('recognitions.given'),
          value: 'given',
        },
        {
          text: this.$t('recognitions.received'),
          value: 'received',
        },
      ]
    },

    dateRangeText() {
      const slashedDates = this.dates.map(date => {
        const dateToFormat = new Date(date).toISOString().slice(0, -1)
        return utils.dates.formatDate(dateToFormat)
      })
      return slashedDates.join(' - ')
    },
  },

  methods: {
    onDateRangeChange(dates) {
      if (!dates) {
        this.filters.date = {}
        this.dates = []
      } else this.filters.date = this.getFormattedDates(dates)
      this.updateQueryFilters()
    },

    updateQueryFilters() {
      const query = {}
      if (this.filters.account) query.account = this.filters.account
      if (this.filters.type) query.type = this.filters.type
      if (this.filters.date.from && this.filters.date.to)
        query.date = this.dateRangeText

      this.navigateTo({
        path: '/recognitions',
        query,
      })
    },

    onTypeChange(type) {
      this.filters.type = type
      this.updateQueryFilters()
    },

    onAccountChange(account) {
      this.selectedAccount = account
      this.filters.account = account?._id

      if (!account) this.filters.type = null
      this.updateQueryFilters()
    },

    isDateAllowed(date) {
      if (date > this.today) return false

      const hasPickedOnlyStartDate = this.dates.length === 1
      return hasPickedOnlyStartDate ? date >= this.dates[0] : true
    },

    parseAndEmitFilters() {
      const payload = {
        filters: {},
      }
      if (this.filters.account && !this.filters.type) {
        payload.allRecognitionsOf = this.filters.account
      }
      if (this.filters.account && this.filters.type) {
        if (this.filters.type === 'given') {
          payload.filters.createdBy = this.filters.account
        } else {
          payload.filters.accounts = [this.filters.account]
        }
      }
      if (this.filters.date?.from && this.filters.date?.to) {
        payload.filters.createdAt = this.filters.date
      }
      this.$emit('filter', payload)
    },

    async searchAccounts(terms = '') {
      const filters = { isDeleted: false }
      if (!terms) filters.site = this.account?.site?._id
      this.isLoadingAccounts = true
      const response = await api.account.getAccounts(this.organizationId, {
        filters,
        terms,
        sort: {
          by: terms ? SORT_BY_COINCIDENCE : DEFAULT_SORT,
          order: 'desc',
        },
      })
      this.availableAccounts = response?.data?.results ?? []
      if (this.filters.account) {
        const queryAccount = await this.findOrGetQueryAccount()
        if (queryAccount) this.availableAccounts.unshift(queryAccount)
      }
      this.isLoadingAccounts = false
    },

    async findOrGetQueryAccount() {
      const existingAccount = this.availableAccounts.some(
        account => account._id === this.filters.account
      )
      if (!existingAccount) {
        const account = await api.account.getById(
          this.organizationId,
          this.filters.account
        )
        return account.data
      }
      return null
    },

    setQueryFilters() {
      const query = this.$route.query
      if (query.account) {
        this.filters.account = query.account
      }
      if (query.type) {
        this.filters.type = query.type
      }
      if (query.date) {
        const dateRange = this.getFormattedDates(query.date.split(' - '))
        this.filters.date = dateRange
        this.dates = [
          moment(dateRange.from).format('YYYY-MM-DD'),
          moment(dateRange.to).format('YYYY-MM-DD'),
        ]
      }
    },

    getFormattedDates(dates) {
      if (!dates.length) return {}
      return {
        from: moment(dates[0])
          .startOf('day')
          .toISOString(),
        to: moment(dates[1])
          .endOf('day')
          .toISOString(),
      }
    },
  },
}
</script>
